/*
  PDP v4 (templates/product.pdp-v4.json for POWR, product.pdp-v4-wondr.json for
  WONDR). Loaded with defer from snippets/pdp-v4-head.liquid, only on those
  templates.

  - Gallery: thumbnails scroll the snap track.
  - Buy box: ladder → price block, member line, ATC label, hidden quantity;
    free-shipping block from the cart + selection; delivery date; ATC via
    PBCart.addLine (one line × N).
  - Section nav: sticky offset under the floating header, active tab follows
    the scroll position, smooth scroll to sections.
  - Proof videos: slide 1 autoplays muted (tap = sound), slide 2 only loads
    when swiped into view.
  - "+ Voeg toe" (geurbooster) and the pakket (3 strips + 2 free boosters,
    replaces an earlier pakket) through PBCart.
  - Bundles (WONDR): ticked add-ons go into the same PBCart.addLines call as the
    bundle; review tabs per component product; the sticky bar can follow the
    buy box ATC instead of the tabs.
  - Fullscreen video viewer (video wall, tip card, how-to video): one <dialog>
    built on first open; a video is only fetched once it is shown.
  - WONDR hair: second size (switches the buy box to another bundle product),
    flip cards per product (coverflow), FAQ category chips.
  - Creator ad code (?code=olivia15, set up by pdp-v4-buybox.liquid): every price
    includes the code, the code goes on the cart, the ad video plays first.

  Every ATC button sits in a form[action=/cart/add] for Dalton's add-to-cart
  count; the click's default is prevented here and the add goes through
  PBCart, so the form never submits.
*/
(function () {
  if (window.__pbPdpV4) return;
  window.__pbPdpV4 = true;

  const PAKKET_KEY = '_pakket';
  const PAKKET_ID_KEY = '_pakket_id';
  // Display only: marks the free booster units in the cart drawer.
  const PAKKET_GIFT_KEY = '_pakket_gift';

  let moneyFormat = '€{{amount_with_comma_separator}}';

  // ponytail: only the two formats this shop can have; amounts < €1.000 here.
  function money(cents) {
    const amount = (Math.round(cents) / 100).toFixed(2);
    return moneyFormat.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) =>
      key.includes('comma') ? amount.replace('.', ',') : amount,
    );
  }

  // "1 doosje" / "3 flessen": the unit words come from the product metafields
  // (custom.volume_ladder_unit_*), set as data attributes by the section.
  function unitName(root, qty) {
    return qty === 1 ? root.dataset.unitSingular || 'doosje' : root.dataset.unitPlural || 'doosjes';
  }

  // Same qualifying subtotal as the cart drawer's progress bar (gift cards and
  // memberships excluded, assets/cart-progress-bar.js); full subtotal as fallback.
  function cartCents(cart) {
    if (typeof window.PBCartQualifyingCents === 'function') return window.PBCartQualifyingCents(cart);
    return Math.round(parseFloat(cart?.cost?.subtotalAmount?.amount || 0) * 100);
  }

  function freeShippingCents() {
    // Same threshold as the cart drawer's free-shipping tier (sections/unified-cart-drawer.liquid).
    const tier = document.querySelector('#CartProgressZone [data-tier][data-reward-type="free_shipping"]');
    const euros = Number(tier?.dataset.threshold);
    return Math.round((Number.isFinite(euros) && euros > 0 ? euros : 30) * 100);
  }

  async function withBusy(button, task) {
    if (button.dataset.pending) return false;
    button.dataset.pending = '1';
    button.classList.add('is-loading');
    button.setAttribute('aria-busy', 'true');
    try {
      await task();
      return true;
    } catch (error) {
      console.error('[PDP v4] toevoegen mislukt', error);
      window.alert('Toevoegen aan je winkelmand lukte niet. Probeer het opnieuw.');
      return false;
    } finally {
      delete button.dataset.pending;
      button.classList.remove('is-loading');
      button.removeAttribute('aria-busy');
    }
  }

  // Every v4 interaction goes to both: the dataLayer (Dalton goals, see powr-card.js)
  // as `pdp_v4_tab_click`, and Converge as "PDP v4 Tab Click". Never blocks the UI.
  function track(event, data = {}) {
    // Brand and format of the page (data-brand / data-format on the buy box) on every event.
    const page = document.querySelector('[data-pdpv4-buy]')?.dataset || {};
    if (page.brand) data = { brand: page.brand, product_format: page.format, ...data };
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event, ...data });
    } catch {
      /* ignore */
    }
    try {
      if (typeof window.cvg !== 'function') return;
      const words = event.replace(/^pdp_v4_/, '').split('_');
      const eventName = `PDP v4 ${words.map((word) => word[0].toUpperCase() + word.slice(1)).join(' ')}`;
      window.cvg({ method: 'track', eventName, properties: data });
    } catch {
      /* ignore */
    }
  }

  // Link and tab clicks, by selector. Navigating links still get their event: the
  // dataLayer push is synchronous and Converge queues before the page unloads.
  const CLICK_EVENTS = [
    ['.pdpv4-nav__tab', 'pdp_v4_tab_click', (el) => ({ tab: el.dataset.pdpv4Tab })],
    ['[data-pdpv4-liquid]', 'pdp_v4_liquid_click', (el) => ({ source: el.dataset.pdpv4Liquid })],
    ['[data-pdpv4-strips]', 'pdp_v4_strips_click', (el) => ({ source: el.dataset.pdpv4Strips })],
    ['[data-pdpv4-pick-mix]', 'pdp_v4_pick_mix_click', (el) => ({ source: el.dataset.pdpv4PickMix })],
    [
      '.pdpv4-wash__tile[data-pdpv4-keep-view]',
      'pdp_v4_wash_switch',
      (el) => ({ to: new URL(el.href, location.origin).pathname.split('/').pop() }),
    ],
  ];

  function initClickTracking() {
    document.addEventListener('click', (event) => {
      for (const [selector, name, props] of CLICK_EVENTS) {
        const el = event.target.closest(selector);
        if (el) {
          track(name, props(el));
          return;
        }
      }
    });
  }

  /* ---------- Preview: keep ?pdpv4_preview=1 on links between the PDPs ---------- */

  function keepPreviewParam(root) {
    if (new URL(location.href).searchParams.get('pdpv4_preview') !== '1') return;
    root.querySelectorAll('[data-pdpv4-keep-view]').forEach((link) => {
      const url = new URL(link.href, location.origin);
      url.searchParams.set('pdpv4_preview', '1');
      link.href = url.pathname + url.search;
    });
  }

  /* ---------- Gallery ---------- */

  function initGallery(root) {
    const track = root.querySelector('[data-pdpv4-gallery-track]');
    const thumbs = [...root.querySelectorAll('[data-pdpv4-thumb]')];
    if (!track || !thumbs.length) return;

    const goTo = (index) => {
      loadAround(index);
      track.scrollTo({ left: index * track.clientWidth, behavior: 'smooth' });
    };
    // Gallery nav (WONDR hair): the "+N" thumbnail unfolds the hidden ones.
    const unfold = root.hasAttribute('data-pdpv4-gallery-nav');
    thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => {
        const more = unfold && thumb.querySelector('.pdpv4-gallery__more');
        if (more) {
          more.remove();
          thumbs.forEach((other) => {
            other.hidden = false;
          });
        }
        goTo(Number(thumb.dataset.pdpv4Thumb));
      });
    });
    const arrows = [...root.querySelectorAll('[data-pdpv4-gallery-step]')];
    const current = () => Math.round(track.scrollLeft / Math.max(track.clientWidth, 1));
    arrows.forEach((arrow) =>
      arrow.addEventListener('click', () => {
        const last = track.children.length - 1;
        goTo(Math.min(last, Math.max(0, current() + Number(arrow.dataset.pdpv4GalleryStep))));
      }),
    );

    // Slides 3+ ship with data-src/data-srcset (pdp-v4-buybox.liquid): load the
    // active slide and the next one, so a swipe never waits on a download.
    const slides = [...track.children];
    // Creator ad video (9:16) before the 1:1 photos: the track takes the active slide's
    // height (animated in CSS), and the video only plays while at least half of it is in view.
    // It starts once its poster (the LCP image, preloaded by the buy box) has loaded, so it never
    // competes with it; at the latest 4 s from here. Waiting for the page's load event cost ~7 s
    // on Fast 4G and ~2 s on a fast connection (measured Oct 2026).
    const video = track.querySelector('.pdpv4-gallery__video');
    const fit = () => {
      if (video) track.style.height = `${slides[Math.min(current(), slides.length - 1)].offsetHeight}px`;
    };
    if (video) {
      fit();
      window.addEventListener('resize', fit);
      let inView = !('IntersectionObserver' in window);
      let started = false;
      const sync = () => {
        if (!started) return;
        if (inView) video.play()?.catch(() => {});
        else video.pause();
      };
      const start = () => {
        started = true;
        sync();
      };
      const poster = new Image();
      poster.onload = start;
      poster.onerror = start;
      poster.src = video.poster;
      if (poster.complete) start();
      setTimeout(start, 4000);
      if (!inView) {
        new IntersectionObserver(
          ([entry]) => {
            inView = entry.isIntersecting;
            sync();
          },
          { threshold: 0.5 },
        ).observe(video);
      }
    }
    const loadAround = (index) =>
      slides.slice(index, index + 2).forEach((slide) => {
        const img = slide.querySelector('img[data-srcset]');
        if (!img) return;
        img.srcset = img.dataset.srcset;
        img.src = img.dataset.src;
        delete img.dataset.srcset;
        delete img.dataset.src;
      });

    let frame = 0;
    track.addEventListener(
      'scroll',
      () => {
        cancelAnimationFrame(frame);
        frame = requestAnimationFrame(() => {
          const index = Math.round(track.scrollLeft / Math.max(track.clientWidth, 1));
          loadAround(index);
          fit();
          const active = Math.min(index, thumbs.length - 1);
          thumbs.forEach((thumb, i) => thumb.classList.toggle('is-active', i === active));
          arrows.forEach((arrow) => {
            const step = Number(arrow.dataset.pdpv4GalleryStep);
            arrow.disabled = step < 0 ? index === 0 : index >= slides.length - 1;
          });
        });
      },
      { passive: true },
    );
  }

  /* ---------- Buy box ---------- */

  // The buy box's add (selection + ticked add-ons), for ATCs outside it (video viewer).
  let buyAdd = null;

  function deliveryLabel() {
    // ponytail: shipped on weekdays before 17:00 (Brussels), delivered the next
    // weekday, no weekend delivery. Replace with the carrier's real rules.
    const parts = new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Europe/Brussels',
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      hourCycle: 'h23',
      weekday: 'short',
    }).formatToParts(new Date());
    const get = (type) => parts.find((part) => part.type === type)?.value;
    const today = new Date(Date.UTC(Number(get('year')), Number(get('month')) - 1, Number(get('day'))));
    const isWeekday = (date) => date.getUTCDay() >= 1 && date.getUTCDay() <= 5;
    const nextWeekday = (date) => {
      const next = new Date(date);
      do next.setUTCDate(next.getUTCDate() + 1);
      while (!isWeekday(next));
      return next;
    };

    const ship = isWeekday(today) && Number(get('hour')) < 17 ? today : nextWeekday(today);
    const delivery = nextWeekday(ship);
    const days = ['zo', 'ma', 'di', 'wo', 'do', 'vr', 'za'];
    const date = `${days[delivery.getUTCDay()]} ${delivery.getUTCDate()}/${delivery.getUTCMonth() + 1}`;
    const tomorrow = (delivery - today) / 86400000 === 1;
    return tomorrow ? `Morgen, ${date} in huis` : `${date.charAt(0).toUpperCase()}${date.slice(1)} in huis`;
  }

  function initBuy(root) {
    if (root.dataset.pdpv4Ready) return;
    root.dataset.pdpv4Ready = '1';
    moneyFormat = root.dataset.moneyFormat || moneyFormat;

    const tiers = [...root.querySelectorAll('.pdpv4-tier__input')];
    const atc = root.querySelector('[data-pdpv4-atc]');
    const sticky = root.querySelector('[data-pdpv4-sticky]');
    const stickyAtc = root.querySelector('[data-pdpv4-sticky-atc]');
    const sheet = root.querySelector('[data-pdpv4-sheet]');
    const sheetTiers = [...root.querySelectorAll('.pdpv4-tier__radio')];
    const desktop = window.matchMedia('(min-width: 990px)');
    const deals = tiers
      .filter((input) => Number(input.dataset.compare) > Number(input.dataset.total))
      .map((input) => Number(input.value));
    const firstDeal = deals.length ? Math.min(...deals) : 0;
    const member = root.querySelector('[data-pdpv4-member]');
    const ship = root.querySelector('[data-pdpv4-ship]');
    const addons = [...root.querySelectorAll('[data-pdpv4-addon]')];
    const addonCents = () =>
      addons.reduce((sum, input) => sum + (input.checked ? Number(input.dataset.price) || 0 : 0), 0);
    let cart = null;

    const selected = () => tiers.find((input) => input.checked);

    function selection() {
      const input = selected();
      if (!input) {
        // No tiers (offer None): variant price from the root, which also exists when sold out.
        const unit = Number(root.dataset.price) || 0;
        const units = Number(root.dataset.units) || 0;
        const memberUnit = Math.round((unit * (100 - (Number(root.dataset.memberDiscount) || 0))) / 100);
        return {
          qty: 1,
          total: unit,
          compare: unit,
          member: memberUnit,
          perWash: units ? Math.round(unit / units) : 0,
        };
      }
      return {
        qty: Number(input.value),
        total: Number(input.dataset.total),
        compare: Number(input.dataset.compare),
        member: Number(input.dataset.member),
        perWash: Number(input.dataset.perWash),
      };
    }

    function renderShip() {
      if (!ship || !cart) return;
      const total = selection().total + addonCents();
      const threshold = freeShippingCents();
      const inCart = cartCents(cart);
      const rest = ship.querySelector('[data-pdpv4-ship-rest]');
      const note = ship.querySelector('[data-pdpv4-ship-note]');
      ship.hidden = false;
      // Reached: keep the block as reassurance instead of hiding it.
      rest.hidden = inCart + total >= threshold;
      if (note) note.hidden = !rest.hidden;
      if (rest.hidden) {
        ship.querySelector('[data-pdpv4-ship-title]').textContent =
          ship.dataset.doneTitle || 'Goeie keuze! Jij krijgt gratis verzending';
        ship.querySelector('[data-pdpv4-ship-fill]').style.width = '100%';
        return;
      }

      // Smallest ladder step that reaches free shipping, in ladder order (high → low).
      const reaching = tiers.filter((input) => inCart + Number(input.dataset.total) >= threshold);
      const step = reaching[reaching.length - 1];
      const qty = step ? Number(step.value) : 0;
      ship.querySelector('[data-pdpv4-ship-title]').textContent = step
        ? `Neem ${qty} ${unitName(root, qty)} → gratis verzending`
        : `Gratis verzending vanaf ${money(threshold)}`;
      rest.textContent = `Nog ${money(threshold - inCart - total)} tot gratis verzending`;
      ship.querySelector('[data-pdpv4-ship-fill]').style.width =
        `${Math.min(100, ((inCart + total) / threshold) * 100)}%`;
    }

    function render() {
      const sel = selection();
      root.querySelectorAll('[data-pdpv4-qty]').forEach((input) => {
        input.value = sel.qty;
      });
      root.querySelector('[data-pdpv4-price-total]').textContent = money(sel.total);
      if (sel.perWash) root.querySelector('[data-pdpv4-price-unit]').textContent = money(sel.perWash);
      root.querySelectorAll('[data-pdpv4-atc-price]').forEach((price) => {
        price.textContent = money(sel.total + addonCents());
      });
      // Creator ad code: the card on the video and the struck price next to the price.
      root.querySelectorAll('[data-pdpv4-creator-price]').forEach((price) => {
        price.textContent = money(sel.total);
      });
      root.querySelectorAll('[data-pdpv4-creator-compare]').forEach((price) => {
        price.textContent = money(sel.compare);
      });

      // Sticky chip + sheet (S15) mirror the ladder: one source of truth.
      sheetTiers.forEach((input) => {
        input.checked = Number(input.value) === sel.qty;
      });
      const stickyTotal = root.querySelector('[data-pdpv4-sticky-total]');
      if (stickyTotal) stickyTotal.textContent = money(sel.total + addonCents());
      const stickyUnit = root.querySelector('[data-pdpv4-sticky-unit]');
      if (stickyUnit && sel.perWash) stickyUnit.textContent = money(sel.perWash);
      const chipTitle = root.querySelector('[data-pdpv4-sticky-qty]');
      if (chipTitle) {
        chipTitle.textContent = `${sel.qty} ${unitName(root, sel.qty)}`;
        const pct = Math.round(((sel.compare - sel.total) / sel.compare) * 100);
        const { dealLabel, dealHint } = root.dataset;
        const hint = firstDeal ? dealHint || `Vanaf ${firstDeal}: korting` : '';
        root.querySelector('[data-pdpv4-sticky-sub]').textContent =
          pct > 0 ? `${dealLabel || `−${pct}%`} · ${money(sel.perWash)}/was` : hint;
      }

      // Members get the higher of member price or ladder price, never both.
      if (member) {
        // Creator ad code: a fixed line ("Members betalen altijd minstens 15% minder") instead.
        member.hidden = !creatorOn() && !(sel.member < sel.total);
        member.dataset.memberPrice = money(sel.member);
        // 2+1: the member discount applies to the paid bottles, so that is the reference price.
        member.dataset.price = money(root.dataset.offer === 'two_plus_one' ? sel.total : sel.compare);
        member.querySelector('[data-pdpv4-member-price]').textContent = money(sel.member);
      }
      renderShip();
    }

    tiers.forEach((input) => input.addEventListener('change', render));
    addons.forEach((input) =>
      input.addEventListener('change', () => {
        track('pdp_v4_addon_toggle', { addon: input.dataset.pdpv4Addon, checked: input.checked });
        render();
      }),
    );

    // Second size (WONDR hair): every ATC of the buy box (and its forms) switches to the
    // picked bundle product; the price block follows through root.dataset.price. The
    // "Welke set past bij jou?" card (pdp-v4-recommendations, data-pdpv4-buy-add / -sel-*)
    // adds the same selection, so its price and Converge data follow too.
    function applySize(input) {
      const option = input.dataset;
      const price = Number(option.price);
      const loose = Number(option.loose);
      root.dataset.price = option.price;
      [...root.querySelectorAll('[data-pdpv4-atc]'), ...document.querySelectorAll('[data-pdpv4-buy-add]')].forEach(
        (button) => {
          Object.assign(button.dataset, {
            variantId: input.value,
            productId: option.productId,
            productTitle: option.productTitle,
            sku: option.sku,
            price: String(price / 100),
          });
          if (option.variantTitle) button.dataset.variantTitle = option.variantTitle;
        },
      );
      root.querySelectorAll('input[name="id"]').forEach((field) => {
        field.value = input.value;
      });
      root.querySelectorAll('[data-pdpv4-loose]').forEach((el) => {
        el.textContent = money(loose);
      });
      root.querySelectorAll('[data-pdpv4-saving]').forEach((el) => {
        el.textContent = money(loose - price);
      });
      const top = root.querySelector('[data-pdpv4-price-top]');
      if (top) top.textContent = money(price);
      document.querySelectorAll('[data-pdpv4-sel-price]').forEach((el) => {
        el.textContent = money(price);
      });
      document.querySelectorAll('[data-pdpv4-sel-loose]').forEach((el) => {
        el.textContent = money(loose);
      });
      document.querySelectorAll('[data-pdpv4-sel-pct]').forEach((el) => {
        el.textContent = String(Math.round(((loose - price) / loose) * 100));
      });
    }
    root.querySelectorAll('[data-pdpv4-size]').forEach((input) =>
      input.addEventListener('change', () => {
        applySize(input);
        track('pdp_v4_size_switch', { size: input.dataset.pdpv4Size });
        render();
      }),
    );
    // The server may start on the second size (first one sold out): bring the card in line.
    const pickedSize = root.querySelector('[data-pdpv4-size]:checked');
    if (pickedSize) applySize(pickedSize);

    // Routine (WONDR Greasy Hair): the checked size and colour radios together name one
    // hidden combination input (bundle product × colour), which goes through applySize.
    const routine = root.querySelector('[data-pdpv4-routine]');
    function applyRoutine() {
      const key = [...routine.querySelectorAll('input[type="radio"]:checked')].map((input) => input.value).join('-');
      const combo = routine.querySelector(`[data-pdpv4-combo="${key}"]`);
      const available = Boolean(combo) && !('soldOut' in combo.dataset);
      if (available) applySize(combo);
      routine.querySelector('[data-pdpv4-routine-sold-out]').hidden = available;
      [...root.querySelectorAll('[data-pdpv4-atc]'), ...document.querySelectorAll('[data-pdpv4-buy-add]')].forEach(
        (button) => {
          button.disabled = !available;
        },
      );
      return key;
    }
    if (routine) {
      routine.addEventListener('change', () => {
        track('pdp_v4_routine_switch', { combo: applyRoutine() });
        render();
      });
      applyRoutine();
    }

    // Bundle + ticked add-ons in one cart call (one drawer open); add-ons are normal lines.
    function add(button) {
      const { qty } = selection();
      const extra = addons.filter((input) => input.checked).map((input) => ({ id: input.value, quantity: 1 }));
      return withBusy(button, () =>
        extra.length
          ? window.PBCart.addLines([{ id: atc.dataset.variantId, quantity: qty }, ...extra])
          : window.PBCart.addLine(atc.dataset.variantId, qty),
      );
    }
    if (atc) {
      buyAdd = add;
      // The card ATCs that add the buy box selection only make sense while it can be bought.
      document.querySelectorAll('[data-pdpv4-buy-add]').forEach((button) => {
        button.hidden = false;
      });
    }

    sheetTiers.forEach((input) =>
      input.addEventListener('change', () => {
        const match = tiers.find((tier) => tier.value === input.value);
        if (match) match.checked = true;
        render();
        // Desktop popover has no ATC of its own: close it so the bar's ATC is one click away.
        if (desktop.matches) sheet.close();
      }),
    );

    // Buy box, sticky bar and sheet ATCs all add the ladder selection.
    root.querySelectorAll('[data-pdpv4-atc]').forEach((button) =>
      button.addEventListener('click', async (event) => {
        event.preventDefault();
        if (!window.PBCart) return;
        const { qty } = selection();
        if (button !== atc) track('pdp_v4_sticky_atc', { qty });
        const added = await add(button);
        if (!added || button === atc || !stickyAtc) return;
        sheet?.close();
        stickyAtc.classList.add('is-added');
        setTimeout(() => stickyAtc.classList.remove('is-added'), 2000);
      }),
    );

    root.querySelector('[data-pdpv4-sheet-open]')?.addEventListener('click', (event) => {
      if (typeof sheet?.showModal !== 'function') return;
      // Desktop: the popover opens upwards, right-aligned with the quantity chip.
      const chip = event.currentTarget.getBoundingClientRect();
      sheet.style.setProperty('--pdpv4-pop-right', `${Math.round(window.innerWidth - chip.right)}px`);
      sheet.showModal();
      track('pdp_v4_sticky_open_sheet');
    });
    sheet?.addEventListener('click', (event) => {
      // A tap on the scrim (the dialog box itself, outside the body) or the grabber closes it.
      if (event.target === sheet || event.target.closest('[data-pdpv4-sheet-close]')) sheet.close();
    });

    // WONDR: the bar shows once the buy box ATC has scrolled up out of view (not on landing,
    // when it is still below the fold), and hides again when you scroll back to it.
    // Checked per scroll frame, not with an observer: a jump past the ATC (hash link, scroll
    // restoration) never makes it intersect, so an observer would not report it.
    if (sticky?.hasAttribute('data-follow-atc') && atc) {
      let atcFrame = 0;
      const checkAtc = () => {
        cancelAnimationFrame(atcFrame);
        atcFrame = requestAnimationFrame(() =>
          document.documentElement.classList.toggle('pdpv4-atc-away', atc.getBoundingClientRect().bottom < 0),
        );
      };
      window.addEventListener('scroll', checkAtc, { passive: true });
      window.addEventListener('resize', checkAtc);
      window.addEventListener('pageshow', checkAtc);
      checkAtc();
    }

    // Never two ATCs in view: the bar steps aside while the Pakket ATC (S10) is on screen.
    const pakketAtc = document.querySelector('[data-pdpv4-pakket-atc]');
    if (sticky && pakketAtc && 'IntersectionObserver' in window) {
      new IntersectionObserver(([entry]) => sticky.classList.toggle('is-off', entry.isIntersecting)).observe(pakketAtc);
    }

    const delivery = root.querySelector('[data-pdpv4-delivery]');
    if (delivery) delivery.textContent = deliveryLabel();

    document.addEventListener('pb:cart:updated', (event) => {
      cart = event.detail?.cart || cart;
      renderShip();
    });
    window.PBCart?.current?.()
      .then((current) => {
        cart = current;
        renderShip();
      })
      .catch(() => {});

    render();
  }

  /* ---------- Creator ad code (?code=olivia15) ---------- */

  const creatorOn = () => document.documentElement.classList.contains('pb-creator');

  // window.PBCreatorActive is set by the inline script in pdp-v4-buybox.liquid (URL or this
  // session, eligible visitors only). Shopify applies the code as an order discount on top of
  // the ladder, so every price shown here is the ladder price minus the code. Runs before
  // initBuy and initPakket, which render from the data attributes rewritten here.
  function initCreator() {
    const active = window.PBCreatorActive;
    const html = document.documentElement;
    // Not active, already done, or the inline fallback already dropped the layer (this file was late).
    if (!active || html.dataset.pbCreatorReady || !html.classList.contains('pb-creator')) return;
    html.dataset.pbCreatorReady = '1';
    if (!window.PBCreatorCode) {
      // Shared module did not load: show the standard page rather than prices without the code.
      html.classList.remove('pb-creator', 'pb-creator-pending');
      return;
    }
    const cut = (cents) => window.PBCreatorCode.price(cents, active.percentage);
    moneyFormat = document.querySelector('[data-pdpv4-buy]')?.dataset.moneyFormat || moneyFormat;

    document.querySelectorAll('[data-pdpv4-creator-pct]').forEach((el) => {
      el.textContent = `−${active.percentage}%`;
    });

    // Ladder: the radios carry the prices initBuy renders from; the labels (buy box + sheet) show them.
    document.querySelectorAll('[data-pdpv4-buy]').forEach((root) => {
      const units = Number(root.dataset.units) || 0;
      const rows = new Map();
      root.querySelectorAll('.pdpv4-tier__input').forEach((input) => {
        const qty = Number(input.value);
        const total = cut(Number(input.dataset.total));
        const perWash = units ? Math.round(total / (qty * units)) : 0;
        input.dataset.total = total;
        input.dataset.perWash = perWash;
        rows.set(input.value, { total, perWash, compare: Number(input.dataset.compare), washes: qty * units });
      });
      root.querySelectorAll('.pdpv4-tier__input, .pdpv4-tier__radio').forEach((input) => {
        const row = rows.get(input.value);
        const label = input.closest('.pdpv4-tier');
        if (!row || !label) return;
        const set = (selector, text) => {
          const el = label.querySelector(selector);
          if (el) el.textContent = text;
        };
        const sheet = input.classList.contains('pdpv4-tier__radio');
        set('.pdpv4-tier__price', money(row.total));
        set('.pdpv4-tier__compare', sheet ? money(row.compare) : `los ${money(row.compare)}`);
        set('.pdpv4-tier__saving', `Bespaar ${money(row.compare - row.total)}`);
        set('.pdpv4-tier__pct', `−${Math.round(((row.compare - row.total) / row.compare) * 100)}%`);
        if (row.washes) set('.pdpv4-tier__sub', `${row.washes} wasbeurten · ${money(row.perWash)} per was`);
      });
    });

    // Pakket (S10): the strips are the paid part; the saving is against the full value.
    document.querySelectorAll('[data-pdpv4-pakket]').forEach((root) => {
      const price = cut(Number(root.dataset.stripsTotal));
      root.querySelectorAll('[data-pdpv4-pakket-price]').forEach((el) => {
        el.textContent = money(price);
      });
      root.querySelectorAll('[data-pdpv4-pakket-saving]').forEach((el) => {
        el.textContent = money(Number(root.dataset.fullTotal) - price);
      });
      const atc = root.querySelector('[data-pdpv4-pakket-atc]');
      if (atc) atc.dataset.labelReady = atc.dataset.labelReady.replace(/·.*$/, `· ${money(price)}`);
    });

    // Recommendation cards (S14): the code is valid on every product. Member lines stay as they are.
    document.querySelectorAll('.pdpv4-recs .powr-card__price-current').forEach((el) => {
      const cents = Number(el.textContent.replace(/\D/g, ''));
      if (cents) el.textContent = money(cut(cents));
    });

    // Single prices elsewhere on the page (S05 refill, S07 geurbooster).
    document.querySelectorAll('[data-pdpv4-creator-cents]').forEach((el) => {
      el.textContent = money(cut(Number(el.dataset.pdpv4CreatorCents)));
    });

    // Prices stay hidden until the code is on the cart. Refused or failed: the plain page,
    // without the code for the rest of the session, so no price is shown that checkout won't give.
    window.PBCreatorCode.apply(active, active.codes).then((applied) => {
      if (applied) {
        html.classList.remove('pb-creator-pending');
        return;
      }
      try {
        sessionStorage.removeItem('pb_creator_code');
      } catch (_) {}
      const url = new URL(location.href);
      url.searchParams.delete('code');
      location.replace(url.toString());
    });
  }

  /* ---------- Section nav ---------- */

  function initNav() {
    const nav = document.querySelector('[data-pdpv4-nav]');
    if (!nav || nav.dataset.pdpv4Ready) return;
    nav.dataset.pdpv4Ready = '1';

    const tabs = [...nav.querySelectorAll('[data-pdpv4-tab]')]
      .map((tab) => {
        const target = document.getElementById(tab.dataset.pdpv4Tab);
        // Measure the section wrapper: it carries content-visibility, so reading it
        // does not force style/layout of a skipped subtree (its descendants would).
        return { tab, target, box: target?.closest('.shopify-section') || target };
      })
      .filter(({ tab, target }) => {
        if (!target) tab.closest('li').hidden = true;
        return Boolean(target);
      });
    const list = nav.querySelector('[data-pdpv4-nav-list]');
    const header = document.querySelector('.pb-unified-nav__fixed > *');
    const wrapper = nav.closest('.shopify-section') || nav;
    let headerBottom = 0;
    let current = null;
    let frame = 0;

    function update() {
      // Once the tabs reach the floating POWR header, the header slides away and the
      // tabs take over the top of the screen (sticky at 0, pdp-v4.css). Measured on
      // the header while it is visible, so hiding it cannot flip the state back.
      // All layout reads first, then the writes: one layout per frame.
      const stuck = document.documentElement.classList.contains('pdpv4-nav-stuck');
      if (header && !stuck) headerBottom = header.getBoundingClientRect().bottom;
      const wrapperTop = wrapper.getBoundingClientRect().top;
      const line = nav.offsetHeight + 24;
      let active = null;
      tabs.forEach((entry) => {
        if (entry.box.getBoundingClientRect().top <= line) active = entry;
      });

      document.documentElement.classList.toggle('pdpv4-nav-stuck', wrapperTop <= headerBottom);
      if (active === current) return;
      current?.tab.removeAttribute('aria-current');
      current = active;
      if (!active) return;
      active.tab.setAttribute('aria-current', 'true');
      const left = active.tab.offsetLeft - (list.clientWidth - active.tab.offsetWidth) / 2;
      list.scrollTo({ left, behavior: 'smooth' });
    }

    const schedule = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(update);
    };
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    update();
  }

  function initAnchors() {
    document.addEventListener('click', (event) => {
      const link = event.target.closest('.pdpv4-nav__tab, [data-pdpv4-anchor]');
      if (!link) return;
      const target = document.getElementById((link.getAttribute('href') || '').slice(1));
      if (!target) return;
      event.preventDefault();
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      // Sections render on approach (content-visibility in pdp-v4.css) and change height
      // mid-scroll, so a long jump can land short: align once more when it stops.
      const settle = () => target.scrollIntoView({ block: 'start' });
      if ('onscrollend' in window) window.addEventListener('scrollend', settle, { once: true });
      else setTimeout(settle, 1000);
    });
  }

  /* ---------- Proof videos ---------- */

  function initVideos(root) {
    const scroller = root.querySelector('[data-pdpv4-videos]');
    if (!scroller || scroller.dataset.pdpv4Ready) return;
    scroller.dataset.pdpv4Ready = '1';

    const slides = [...scroller.querySelectorAll('[data-pdpv4-video-slide]')];
    const dots = [...root.querySelectorAll('[data-pdpv4-dot]')];

    slides.forEach((slide) => {
      const video = slide.querySelector('video');
      if (!video) return;
      const sync = () => {
        slide.classList.toggle('is-playing', !video.paused);
        slide.classList.toggle('is-muted', video.muted);
      };
      ['play', 'pause', 'volumechange'].forEach((type) => video.addEventListener(type, sync));
      sync();

      slide.querySelector('[data-pdpv4-play]')?.addEventListener('click', () => {
        if (video.paused || video.muted) {
          track('pdp_v4_video_play', { video: slides.indexOf(slide) + 1 });
        }
        if (video.paused) {
          video.muted = false;
          delete video.dataset.pdpv4Stopped;
          video.play().catch(() => {});
        } else if (video.muted) {
          video.muted = false;
        } else {
          video.pause();
          video.dataset.pdpv4Stopped = '1';
        }
      });
    });

    const setPosters = () =>
      scroller.querySelectorAll('video[data-poster]').forEach((video) => {
        video.poster = video.dataset.poster;
      });
    if (!('IntersectionObserver' in window)) {
      setPosters();
      return;
    }

    // Load speed: nothing is fetched until the carousel is near the viewport, and only the
    // visible slide of an on-screen carousel loads or plays (the rest pauses).
    // Slides at least 60% visible in the scroller: one on mobile, both side by side on desktop.
    const visible = new Set();
    let onScreen = false;
    function update() {
      slides.forEach((slide) => {
        const video = slide.querySelector('video');
        if (!video) return;
        if (onScreen && visible.has(slide)) {
          if (video.readyState === 0) {
            video.preload = 'metadata';
            video.load();
          }
          if ('pdpv4Autoplay' in video.dataset && video.paused && !video.dataset.pdpv4Stopped) {
            video.play().catch(() => {});
          }
        } else if (!video.paused) {
          video.pause();
        }
      });
    }

    new IntersectionObserver(
      (entries, observer) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        setPosters();
        observer.disconnect();
      },
      { rootMargin: '800px 0px' },
    ).observe(scroller);
    new IntersectionObserver(
      ([entry]) => {
        onScreen = entry.isIntersecting;
        update();
      },
      { threshold: 0.3 },
    ).observe(scroller);
    const slideObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.6) visible.add(entry.target);
          else visible.delete(entry.target);
        });
        const index = slides.findIndex((slide) => visible.has(slide));
        if (index >= 0) dots.forEach((dot, i) => dot.classList.toggle('is-active', i === index));
        update();
      },
      { root: scroller, threshold: [0, 0.6] },
    );
    slides.forEach((slide) => slideObserver.observe(slide));
  }

  /* ---------- "+ Voeg toe" (geurbooster) ---------- */

  function initAddButtons() {
    document.addEventListener('click', (event) => {
      // "Welke set past bij jou?" card: adds what the buy box has selected (size included).
      const buyButton = event.target.closest('[data-pdpv4-buy-add]');
      if (buyButton && buyAdd) {
        event.preventDefault();
        buyAdd(buyButton);
        return;
      }
      const button = event.target.closest('[data-pdpv4-add]');
      if (!button) return;
      event.preventDefault();
      if (!window.PBCart) return;
      withBusy(button, () => window.PBCart.addLine(button.dataset.pdpv4Add, 1));
    });
  }

  /* ---------- Pakketdeal ---------- */

  function initPakket(root) {
    if (root.dataset.pdpv4Ready) return;
    root.dataset.pdpv4Ready = '1';

    const count = Number(root.dataset.count) || 3;
    const steppers = [...root.querySelectorAll('[data-pdpv4-stepper]')];
    const counter = root.querySelector('[data-pdpv4-pakket-counter]');
    const atc = root.querySelector('[data-pdpv4-pakket-atc]');
    const qtyOf = (stepper) => Number(stepper.querySelector('[data-pdpv4-step-qty]').textContent) || 0;
    const total = () => steppers.reduce((sum, stepper) => sum + qtyOf(stepper), 0);
    // A sold-out booster makes the whole deal unavailable (pdp-v4-pakket.liquid).
    const boostersOut = root.hasAttribute('data-boosters-out');
    const soldOutPicked = () => steppers.some((stepper) => stepper.hasAttribute('data-sold-out') && qtyOf(stepper) > 0);

    function render() {
      const sum = total();
      steppers.forEach((stepper) => {
        const qty = qtyOf(stepper);
        stepper.classList.toggle('is-active', qty > 0);
        stepper.querySelector('[data-pdpv4-step="-1"]').disabled = qty <= 0;
        stepper.querySelector('[data-pdpv4-step="1"]').disabled = sum >= count || stepper.hasAttribute('data-sold-out');
      });
      counter.textContent = sum === count ? `${sum}/${count} ✓` : `${sum}/${count}`;
      const missing = count - sum;
      atc.disabled = missing > 0 || boostersOut || soldOutPicked();
      if (boostersOut) atc.textContent = 'Pakket tijdelijk niet beschikbaar';
      else if (missing > 0) atc.textContent = `Kies nog ${missing} ${unitName(root, missing)}`;
      else atc.textContent = atc.dataset.labelReady;
    }

    steppers.forEach((stepper) => {
      stepper.addEventListener('click', (event) => {
        const button = event.target.closest('[data-pdpv4-step]');
        if (!button || button.disabled) return;
        const output = stepper.querySelector('[data-pdpv4-step-qty]');
        output.textContent = Math.max(0, qtyOf(stepper) + Number(button.dataset.pdpv4Step));
        render();
      });
    });

    atc.addEventListener('click', (event) => {
      event.preventDefault();
      if (!window.PBCart || atc.disabled || total() !== count) return;
      withBusy(atc, async () => {
        const id = `pakket_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
        const attributes = [
          { key: PAKKET_KEY, value: root.dataset.pakketKey },
          { key: PAKKET_ID_KEY, value: id },
        ];
        const items = steppers
          .filter((stepper) => qtyOf(stepper) > 0)
          .map((stepper) => ({ id: stepper.dataset.variantId, quantity: qtyOf(stepper), attributes }));
        root.querySelectorAll('[data-pdpv4-booster]').forEach((booster) => {
          items.push({
            id: booster.dataset.pdpv4Booster,
            quantity: 1,
            attributes: [...attributes, { key: PAKKET_GIFT_KEY, value: 'true' }],
          });
        });

        // One pakket per cart: a new one replaces the old one, but only once the new
        // one is in the cart in full. A failed or partial add keeps the old pakket.
        // The discount app's cart transform merges a complete pakket into one line;
        // its components keep their own attributes, and the merged line is what
        // gets removed.
        const linesOf = (cart) =>
          (cart?.lines?.edges || []).flatMap(({ node }) =>
            node.lineComponents?.length
              ? node.lineComponents.map((component) => ({ ...component, removeId: node.id }))
              : [{ ...node, removeId: node.id }],
          );
        const attr = (line, key) => (line.attributes || []).find((attribute) => attribute.key === key)?.value;
        const removeIds = (lines) => [...new Set(lines.map((line) => line.removeId))];

        const lines = linesOf(await window.PBCart.addLines(items));
        const added = lines.filter((line) => attr(line, PAKKET_ID_KEY) === id);
        const complete = items.every((item) =>
          added.some((line) => String(line.merchandise?.id).endsWith(`/${item.id}`) && line.quantity >= item.quantity),
        );
        if (!complete) {
          await window.PBCart.removeLines(removeIds(added));
          throw new Error('[PDP v4] pakket not complete after add (stock?)');
        }
        const old = removeIds(lines.filter((line) => attr(line, PAKKET_KEY) && attr(line, PAKKET_ID_KEY) !== id));
        if (old.length) await window.PBCart.removeLines(old);
        track('pdp_v4_pakket_atc', {
          // e.g. "Witte was:0,Donkere was:1,Gekleurde was:2"
          mix: steppers
            .map((stepper) => {
              const label = stepper.closest('.pdpv4-stepper-row')?.querySelector('.pdpv4-serif-title');
              return `${label?.textContent.trim() || stepper.dataset.variantId}:${qtyOf(stepper)}`;
            })
            .join(','),
        });
      });
    });

    render();
  }

  /* ---------- Reviews (Reviews.io widget API, public: store id, no key) ---------- */

  const REVIEWS_API = 'https://api.reviews.io/timeline/data';
  const MIN_REVIEWS_FOR_DETAIL = 3;
  const REVIEWS_PAGE = 3;
  const reviewsCache = new Map();

  // One request per distinct query per page view: the buy box and S09 share the first page.
  function fetchReviews(store, sku, params) {
    const url = new URL(REVIEWS_API);
    Object.entries({ type: 'product_review', store, sku, ...params }).forEach(([key, value]) =>
      url.searchParams.set(key, value),
    );
    // String, not URL: the fetch wrapper in layout/theme.liquid calls url.includes().
    const key = url.toString();
    if (!reviewsCache.has(key)) {
      const request = fetch(key).then((response) => {
        if (!response.ok) throw new Error(`Reviews.io ${response.status}`);
        return response.json();
      });
      request.catch(() => reviewsCache.delete(key));
      reviewsCache.set(key, request);
    }
    return reviewsCache.get(key);
  }

  const firstPage = { sort: 'date_desc', page: 1, per_page: REVIEWS_PAGE };
  // Bundles set data-decimals="1" on S09 ("4,7"); otherwise Reviews.io's own string.
  const formatRating = (value) => {
    const decimals = Number(document.querySelector('[data-pdpv4-reviews][data-store]')?.dataset.decimals);
    const number = Number(value);
    return (Number.isFinite(decimals) && Number.isFinite(number) ? number.toFixed(decimals) : String(value)).replace(
      '.',
      ',',
    );
  };

  // Buy box: same numbers as S09, and a quote when the merchant set none.
  async function initReviewSummary() {
    const source = document.querySelector('[data-pdpv4-reviews]');
    const buy = document.querySelector('[data-pdpv4-buy]');
    if (!source?.dataset.store || !source.dataset.sku || !buy || buy.dataset.pdpv4Reviews) return;
    buy.dataset.pdpv4Reviews = '1';
    const { store, sku } = source.dataset;

    try {
      const data = await fetchReviews(store, sku, firstPage);
      const count = Number(data.stats?.review_count) || 0;
      if (count) {
        buy.querySelectorAll('[data-pdpv4-rating-score]').forEach((el) => {
          el.textContent = formatRating(data.stats.average_rating);
        });
        buy.querySelectorAll('[data-pdpv4-rating-count]').forEach((el) => {
          el.textContent = String(count);
        });
      }
    } catch (error) {
      window.PBDebug?.warn('[PDP v4] review score niet geladen', error);
    }

    const quote = buy.querySelector('[data-pdpv4-auto-quote]');
    if (!quote) return;
    try {
      // Reviews.io groups the three colours: keep only this product's own SKU.
      const data = await fetchReviews(store, sku, { sort: 'highest_rating', page: 1, per_page: 30 });
      const pick = (data.timeline || [])
        .map((item) => item._source || {})
        .filter((review) => review.sku === sku && Number(review.rating) === 5)
        .map((review) => ({ ...review, text: String(review.comments || '').trim() }))
        .filter((review) => review.text.length >= 20 && review.text.length <= 140)
        .sort((a, b) => String(b.date_created).localeCompare(String(a.date_created)))[0];
      if (!pick) return;
      const city = String(pick.address || '')
        .split(',')[0]
        .trim();
      quote.querySelector('[data-pdpv4-quote-text]').textContent = `“${pick.text}”`;
      // A bare country code ("NL") is not a place to come from.
      quote.querySelector('[data-pdpv4-quote-author]').textContent =
        city.length > 3 ? `${pick.author} uit ${city}` : pick.author;
      quote.hidden = false;
    } catch (error) {
      window.PBDebug?.warn('[PDP v4] review quote niet geladen', error);
    }
  }

  function initReviews(root) {
    if (root.dataset.pdpv4Ready || !root.dataset.store || !root.dataset.sku) return;
    root.dataset.pdpv4Ready = '1';

    const list = root.querySelector('[data-pdpv4-rv-list]');
    const more = root.querySelector('[data-pdpv4-rv-more]');
    const template = root.querySelector('[data-pdpv4-rv-template]');
    const star = root.querySelector('[data-pdpv4-rv-star]');
    const chips = [...root.querySelectorAll('[data-pdpv4-rv-sort]')];
    // Bundles: one tab per component product ("Alle" = all their SKUs), and an
    // "Over: <product>" tag from each review's SKU while "Alle" is selected.
    const tabs = [...root.querySelectorAll('[data-pdpv4-rv-tab]')];
    let labels = {};
    try {
      labels = JSON.parse(root.querySelector('[data-pdpv4-rv-labels]')?.textContent || '{}');
    } catch {
      /* no tags */
    }
    const dateFormat = new Intl.DateTimeFormat('nl-BE', { day: 'numeric', month: 'long', year: 'numeric' });
    let sku = root.dataset.sku;
    let sort = 'date_desc';
    let page = 0;
    let total = 0;
    // Bumped on every sort change; a response from an older generation is dropped.
    let generation = 0;

    function renderStats(data) {
      const count = Number(data.stats?.review_count) || 0;
      const buckets = data.aggregations?.ratings?.buckets || [];
      if (!count || !buckets.length) return;
      root.querySelector('[data-pdpv4-rv-score]').textContent = formatRating(data.stats.average_rating);
      root.querySelector('[data-pdpv4-rv-count]').textContent = root.dataset.countText
        ? root.dataset.countText.replace('[count]', count)
        : [`${count} reviews`, root.dataset.scope, 'Reviews.io'].filter(Boolean).join(' · ');
      // A distribution of one or two reviews says nothing.
      if (count < MIN_REVIEWS_FOR_DETAIL || !root.querySelector('[data-pdpv4-rv-dist]')) return;
      root.querySelectorAll('.pdpv4-reviews__bar').forEach((bar) => {
        const docs = buckets.find((bucket) => String(bucket.key) === bar.dataset.star)?.doc_count || 0;
        const pct = Math.round((docs / count) * 100);
        bar.querySelector('[data-pdpv4-rv-fill]').style.width = `${pct}%`;
        bar.querySelector('[data-pdpv4-rv-pct]').textContent = `${pct}%`;
      });
      root.querySelector('[data-pdpv4-rv-dist]').hidden = false;
    }

    function renderReview(source) {
      const card = template.content.firstElementChild.cloneNode(true);
      const stars = card.querySelector('[data-rv-stars]');
      for (let i = 1; i <= 5; i += 1) {
        const icon = star.content.firstElementChild.cloneNode(true);
        if (i > Number(source.rating)) icon.classList.add('is-off');
        stars.append(icon);
      }
      stars.setAttribute('aria-label', `${source.rating} van 5 sterren`);
      const date = new Date(String(source.date_created).replace(' ', 'T'));
      const dateEl = card.querySelector('[data-rv-date]');
      if (dateEl) dateEl.textContent = Number.isNaN(date.getTime()) ? '' : dateFormat.format(date);
      const tag = card.querySelector('[data-rv-tag]');
      if (tag && sku === root.dataset.sku && labels[source.sku]) {
        tag.textContent = `Over: ${labels[source.sku]}`;
        tag.hidden = false;
      }
      // Review text comes from customers: textContent only, never innerHTML.
      const text = String(source.comments || '').trim();
      const textEl = card.querySelector('[data-rv-text]');
      if (text) textEl.textContent = `“${text}”`;
      else textEl.remove();
      const city = String(source.address || '')
        .split(',')[0]
        .trim();
      card.querySelector('[data-rv-author]').textContent = [source.author, city].filter(Boolean).join(' · ');
      list.append(card);
    }

    async function show(reset) {
      if (reset) {
        generation += 1;
        page = 0;
        list.replaceChildren();
      }
      const run = { generation, sort, page, sku };
      more.disabled = true;
      try {
        const data = await fetchReviews(root.dataset.store, run.sku, {
          sort: run.sort,
          page: run.page + 1,
          per_page: REVIEWS_PAGE,
        });
        if (run.generation !== generation) return;
        if (run.page === 0 && run.sort === 'date_desc' && reset === true) renderStats(data);
        total = Number(data.stats?.results_count) || 0;
        (data.timeline || []).forEach((item) => renderReview(item._source || {}));
        page = run.page + 1;
        list.hidden = !list.children.length;
        const filters = root.querySelector('[data-pdpv4-rv-filters]');
        if (filters) filters.hidden = total < MIN_REVIEWS_FOR_DETAIL;
        more.hidden = page * REVIEWS_PAGE >= total;
      } catch (error) {
        window.PBDebug?.warn('[PDP v4] reviews niet geladen', error);
      } finally {
        if (run.generation === generation) more.disabled = false;
      }
    }

    chips.forEach((chip) => {
      chip.addEventListener('click', () => {
        if (chip.dataset.pdpv4RvSort === sort) return;
        sort = chip.dataset.pdpv4RvSort;
        track('pdp_v4_reviews_sort', { sort });
        chips.forEach((other) => other.setAttribute('aria-pressed', String(other === chip)));
        show('sort');
      });
    });
    tabs.forEach((tab) => {
      tab.addEventListener('click', () => {
        if (tab.dataset.pdpv4RvTab === sku) return;
        sku = tab.dataset.pdpv4RvTab;
        track('pdp_v4_review_tab', { product: tab.dataset.label || 'alle' });
        tabs.forEach((other) => other.setAttribute('aria-pressed', String(other === tab)));
        show('tab');
      });
    });
    more.addEventListener('click', () => {
      track('pdp_v4_reviews_more', { page: page + 1, sort });
      show(false);
    });

    // Load when the section comes near the viewport, not on page load.
    if (!('IntersectionObserver' in window)) {
      show(true);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        observer.disconnect();
        show(true);
      },
      { rootMargin: '600px 0px' },
    );
    observer.observe(root);
  }

  /* ---------- Video wall + fullscreen viewer ---------- */

  // Wall rail: the bar under the cards shows the visible part of the rail.
  function initWall(root) {
    const rail = root.querySelector('[data-pdpv4-wall-rail]');
    const bar = root.querySelector('[data-pdpv4-wall-progress]');
    if (!rail || !bar || rail.dataset.pdpv4Ready) return;
    rail.dataset.pdpv4Ready = '1';
    let frame = 0;
    const update = () => {
      const { scrollWidth, clientWidth, scrollLeft } = rail;
      bar.style.width = `${Math.min(100, (clientWidth / Math.max(scrollWidth, 1)) * 100)}%`;
      bar.style.transform = `translateX(${(scrollLeft / Math.max(clientWidth, 1)) * 100}%)`;
    };
    const schedule = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(update);
    };
    root
      .querySelectorAll('[data-pdpv4-wall-step]')
      .forEach((button) =>
        button.addEventListener('click', () =>
          rail.scrollBy({ left: Number(button.dataset.pdpv4WallStep) * rail.clientWidth * 0.8, behavior: 'smooth' }),
        ),
      );
    rail.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    // First measurement once the wall comes near: reading the rail while its section is
    // skipped (content-visibility) would force that section's layout during boot.
    if (!('IntersectionObserver' in window)) {
      update();
      return;
    }
    const near = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        near.disconnect();
        schedule();
      },
      { rootMargin: '300px 0px' },
    );
    near.observe(root.closest('.shopify-section') || root);
  }

  // "Name · #0282" → "0282" (the creative's code, for tracking).
  const videoCode = (label) => (String(label || '').match(/#(\w+)/) || [])[1] || '';

  let viewer = null;

  // One <dialog> for every playlist on the page, built on first open. Modal: Esc closes it,
  // the page behind is inert and focus returns to the opener on close.
  function buildViewer() {
    const dialog = document.createElement('dialog');
    dialog.className = 'pdpv4 pdpv4-viewer';
    dialog.setAttribute('aria-label', "Video's");
    dialog.innerHTML = `
      <div class="pdpv4-viewer__stage">
        <video class="pdpv4-viewer__video" playsinline muted preload="none"></video>
        <div class="pdpv4-viewer__bars" aria-hidden="true"></div>
        <div class="pdpv4-viewer__top">
          <p class="pdpv4-viewer__label"></p>
          <button type="button" class="pdpv4-viewer__close" aria-label="Sluiten">✕</button>
        </div>
        <p class="pdpv4-viewer__quote"></p>
        <button type="button" class="pdpv4-viewer__zone pdpv4-viewer__zone--prev" aria-label="Vorige video"></button>
        <button type="button" class="pdpv4-viewer__zone pdpv4-viewer__zone--toggle" aria-label="Pauzeer video"></button>
        <button type="button" class="pdpv4-viewer__zone pdpv4-viewer__zone--next" aria-label="Volgende video"></button>
        <p class="pdpv4-viewer__status" aria-live="polite"></p>
        <div class="pdpv4-viewer__bottom">
          <button type="button" class="pdpv4-viewer__sound">Tik voor geluid</button>
          <div class="pdpv4-viewer__product">
            <span class="pdpv4-viewer__thumb"></span>
            <span class="pdpv4-viewer__product-text">
              <span class="pdpv4-viewer__product-title"></span>
              <span class="pdpv4-viewer__product-meta"></span>
            </span>
            <form action="/cart/add" method="post" class="pdpv4-viewer__form">
              <input type="hidden" name="id">
              <input type="hidden" name="quantity" value="1">
              <button type="submit" class="pdpv4-viewer__atc"></button>
            </form>
          </div>
        </div>
      </div>`;
    document.body.append(dialog);

    const video = dialog.querySelector('video');
    const bars = dialog.querySelector('.pdpv4-viewer__bars');
    const sound = dialog.querySelector('.pdpv4-viewer__sound');
    const state = { list: [], index: 0 };
    let renderMeta = () => {};

    // Playback state, shown in the middle of the video: loading (spinner), paused (play),
    // ended (replay, last video), error (retry). Nothing while it plays.
    const toggle = dialog.querySelector('.pdpv4-viewer__zone--toggle');
    const status = dialog.querySelector('.pdpv4-viewer__status');
    const STATUS = {
      loading: ['Video laden…', 'Pauzeer video'],
      playing: ['', 'Pauzeer video'],
      paused: ['Gepauzeerd', 'Speel video af'],
      ended: ['Opnieuw bekijken', 'Bekijk opnieuw'],
      error: ['Deze video laadt niet. Tik om opnieuw te proberen.', 'Probeer opnieuw'],
    };
    function setStatus(name) {
      dialog.dataset.status = name;
      status.textContent = STATUS[name][0];
      toggle.setAttribute('aria-label', STATUS[name][1]);
    }
    const play = () =>
      video.play().catch((error) => {
        // Autoplay refused: show the play button. AbortError = interrupted by the next load.
        if (error?.name !== 'AbortError' && dialog.dataset.status !== 'error') setStatus('paused');
      });

    // The product card mirrors the buy box: same title, price, image, ATC data (Converge).
    const buy = document.querySelector('[data-pdpv4-buy]');
    const buyAtc = buy?.querySelector('[data-pdpv4-atc]');
    const card = dialog.querySelector('.pdpv4-viewer__product');
    const atc = dialog.querySelector('.pdpv4-viewer__atc');
    if (buy && buyAtc) {
      const title = buy.querySelector('.pdpv4-buy__title')?.textContent.trim() || '';
      const parts = buy.querySelectorAll('.pdpv4-parts__item').length;
      dialog.querySelector('.pdpv4-viewer__product-title').textContent = title;
      // Read from the buy box ATC on every open: it already includes the ticked add-ons.
      renderMeta = () => {
        const price = buy.querySelector('[data-pdpv4-atc-price]')?.textContent.trim();
        const extras = buy.querySelectorAll('[data-pdpv4-addon]:checked').length;
        dialog.querySelector('.pdpv4-viewer__product-meta').textContent = [
          price || money(Number(buy.dataset.price) || 0),
          parts ? `${parts} producten` : '',
          extras ? `+ ${extras} extra` : '',
        ]
          .filter(Boolean)
          .join(' · ');
      };
      const image = buy.querySelector('.pdpv4-gallery__slide img');
      if (image) {
        const thumb = document.createElement('img');
        thumb.src = image.currentSrc || image.src || image.dataset.src || '';
        thumb.alt = '';
        dialog.querySelector('.pdpv4-viewer__thumb').append(thumb);
      }
      [...buyAtc.attributes]
        .filter((attr) => attr.name.startsWith('data-') && attr.name !== 'data-pdpv4-atc')
        .forEach((attr) => atc.setAttribute(attr.name, attr.value));
      atc.dataset.atcSource = 'pdp_v4_viewer';
      atc.textContent = buy.dataset.atcLabel || 'In winkelwagen';
      dialog.querySelector('input[name="id"]').value = buyAtc.dataset.variantId || '';
    } else {
      card.hidden = true;
    }

    function show(index) {
      state.index = Math.max(0, Math.min(index, state.list.length - 1));
      const entry = state.list[state.index] || {};
      video.pause();
      video.replaceChildren(
        ...(entry.sources || []).map((source) => {
          const el = document.createElement('source');
          el.src = source.src;
          if (source.type) el.type = source.type;
          return el;
        }),
      );
      video.poster = entry.poster || '';
      setStatus('loading');
      video.load();
      play();
      dialog.querySelector('.pdpv4-viewer__label').textContent = (entry.label || '').split(' · #')[0];
      const quote = dialog.querySelector('.pdpv4-viewer__quote');
      quote.textContent = entry.quote ? `“${entry.quote}”` : '';
      quote.hidden = !entry.quote;
      [...bars.children].forEach((bar, i) => {
        bar.firstChild.style.width = i < state.index ? '100%' : '0%';
      });
      dialog.querySelector('.pdpv4-viewer__zone--prev').hidden = state.index === 0;
    }

    function next() {
      if (state.index < state.list.length - 1) show(state.index + 1);
    }

    // Decided on the media state, not the shown status: a pause at 0 s (before the first
    // frame, or while loading) gets no 'pause' status from the listener below.
    toggle.addEventListener('click', () => {
      const current = dialog.dataset.status;
      if (current === 'error') show(state.index);
      else if (current === 'ended') {
        video.currentTime = 0;
        play();
      } else if (video.paused) play();
      else {
        video.pause();
        setStatus('paused');
      }
    });

    video.addEventListener('timeupdate', () => {
      const bar = bars.children[state.index];
      if (bar && video.duration) bar.firstChild.style.width = `${(video.currentTime / video.duration) * 100}%`;
    });
    video.addEventListener('ended', () => {
      if (state.index < state.list.length - 1) next();
      else setStatus('ended');
    });
    video.addEventListener('playing', () => setStatus('playing'));
    video.addEventListener('waiting', () => setStatus('loading'));
    // A pause that is not a video switch (load() resets the time to 0) or the close.
    video.addEventListener('pause', () => {
      if (dialog.open && !video.ended && video.currentTime > 0) setStatus('paused');
    });
    // <source> errors do not bubble: catch them on the way down. No source left = failed.
    video.addEventListener(
      'error',
      () => {
        if (video.networkState === HTMLMediaElement.NETWORK_NO_SOURCE || video.error) setStatus('error');
      },
      true,
    );
    video.addEventListener('volumechange', () => {
      sound.hidden = !video.muted;
    });
    sound.addEventListener('click', () => {
      video.muted = false;
      play();
    });
    dialog.querySelector('.pdpv4-viewer__zone--prev').addEventListener('click', () => show(state.index - 1));
    dialog.querySelector('.pdpv4-viewer__zone--next').addEventListener('click', next);
    dialog.querySelector('.pdpv4-viewer__close').addEventListener('click', () => dialog.close());

    // Swipe left/right = next/previous video.
    let startX = null;
    dialog.addEventListener(
      'touchstart',
      (event) => {
        startX = event.touches[0].clientX;
      },
      { passive: true },
    );
    dialog.addEventListener(
      'touchend',
      (event) => {
        if (startX === null) return;
        const dx = event.changedTouches[0].clientX - startX;
        startX = null;
        if (Math.abs(dx) < 50) return;
        if (dx < 0) next();
        else show(state.index - 1);
      },
      { passive: true },
    );

    // Closing stops the download as well as the playback.
    dialog.addEventListener('close', () => {
      video.pause();
      video.replaceChildren();
      video.removeAttribute('poster');
      video.load();
      document.documentElement.classList.remove('pdpv4-viewer-open');
    });

    // Same add as the buy box (selection + add-ons), after the viewer is out of the way of the drawer.
    atc.addEventListener('click', (event) => {
      event.preventDefault();
      if (!buyAdd || !window.PBCart) return;
      track('pdp_v4_video_viewer_atc', { code: videoCode(state.list[state.index]?.label) });
      dialog.close();
      buyAdd(atc);
    });

    return {
      open(list, index) {
        state.list = list;
        bars.replaceChildren(
          ...list.map(() => {
            const bar = document.createElement('span');
            bar.append(document.createElement('span'));
            return bar;
          }),
        );
        bars.hidden = list.length < 2;
        video.muted = true;
        sound.hidden = false;
        renderMeta();
        if (!dialog.open) dialog.showModal();
        document.documentElement.classList.add('pdpv4-viewer-open');
        show(index);
      },
    };
  }

  /* ---------- Inline video (Zo scheer je) ---------- */

  // The video has preload="none": the first tap starts loading and playing in place.
  function initInlinePoster(video) {
    if (!('IntersectionObserver' in window)) {
      video.poster = video.dataset.poster;
      return;
    }
    const near = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        near.disconnect();
        video.poster = video.dataset.poster;
      },
      { rootMargin: '400px 0px' },
    );
    near.observe(video.closest('.shopify-section') || video);
  }

  function initInlineVideo() {
    const mobile = window.matchMedia('(max-width: 989px)');
    document.addEventListener('click', (event) => {
      // Small video next to the steps (WONDR hair): on mobile any tap opens it fullscreen.
      const small = event.target.closest('[data-pdpv4-viewer-mobile]');
      if (small && mobile.matches && openViewer(small, 0)) {
        event.preventDefault();
        return;
      }
      const button = event.target.closest('[data-pdpv4-inline-play]');
      const wrap = button?.closest('[data-pdpv4-inline-video]');
      const video = wrap?.querySelector('video');
      if (!video) return;
      wrap.classList.add('is-playing');
      video.controls = true;
      video.play().catch(() => {});
      video.focus();
      track('pdp_v4_usage_video_play', {});
    });
  }

  // Opens the playlist of a [data-pdpv4-viewer-scope]; false when it has none.
  function openViewer(scope, index) {
    let list = [];
    try {
      list = JSON.parse(scope?.querySelector('[data-pdpv4-playlist]')?.textContent || '[]');
    } catch {
      return false;
    }
    if (!list.length) return false;
    viewer = viewer || buildViewer();
    viewer.open(list, index);
    track('pdp_v4_video_open', { index: index + 1, code: videoCode(list[index]?.label) });
    return true;
  }

  function initViewer() {
    document.addEventListener('click', (event) => {
      const opener = event.target.closest('[data-pdpv4-viewer-open]');
      if (!opener) return;
      const index = Number(opener.dataset.pdpv4ViewerOpen) || 0;
      if (openViewer(opener.closest('[data-pdpv4-viewer-scope]'), index)) event.preventDefault();
    });
  }

  /* ---------- Flip cards per product (WONDR hair) ---------- */

  // Coverflow: the middle card is data-pos 0, its neighbours ±1 / ±2 (wrapping around),
  // the rest "far" (hidden). Tap the middle card = turn it over, tap a side card = bring
  // it to the middle. Cards hidden by CSS (the set card on mobile) are left out.
  function initFlip(root) {
    if (root.dataset.pdpv4Ready) return;
    root.dataset.pdpv4Ready = '1';
    const all = [...root.querySelectorAll('[data-pdpv4-flip-card]')];
    const dots = [...root.querySelectorAll('[data-pdpv4-flip-go]')];
    const rail = root.querySelector('[data-pdpv4-flip-track]');
    let cards = all;
    // The first product card (after the set card) starts in the middle.
    let current = all[1] || all[0];
    let swiped = false;

    function setFlipped(card, flipped) {
      card.classList.toggle('is-flipped', flipped);
      card.querySelector('.pdpv4-flip__back').inert = !flipped;
      card.querySelector('.pdpv4-flip__front').inert = flipped;
    }

    function layout() {
      cards = all.filter((card) => getComputedStyle(card).display !== 'none');
      const count = cards.length;
      if (!count) return;
      if (!cards.includes(current)) current = cards[0];
      const active = cards.indexOf(current);
      cards.forEach((card, index) => {
        let offset = index - active;
        if (offset > count / 2) offset -= count;
        if (offset < -count / 2) offset += count;
        card.dataset.pos = Math.abs(offset) <= 2 ? String(offset) : 'far';
        if (offset !== 0) setFlipped(card, false);
      });
      dots.forEach((dot) => {
        dot.setAttribute('aria-current', String(all[Number(dot.dataset.pdpv4FlipGo)] === current));
      });
    }

    function step(by) {
      const count = cards.length;
      current = cards[(((cards.indexOf(current) + by) % count) + count) % count];
      layout();
    }

    root.addEventListener('click', (event) => {
      if (swiped) {
        swiped = false;
        return;
      }
      const card = event.target.closest('[data-pdpv4-flip-toggle]')?.closest('[data-pdpv4-flip-card]');
      if (card) {
        if (card !== current) {
          current = card;
          layout();
          return;
        }
        const flipped = !card.classList.contains('is-flipped');
        setFlipped(card, flipped);
        // Keep focus on the visible face for keyboard users, once it is visible
        // (the faces swap visibility halfway through the turn, pdp-v4-wondr-v2.css).
        setTimeout(() => card.querySelector(flipped ? '.pdpv4-flip__back-turn' : '.pdpv4-flip__front')?.focus(), 260);
        if (flipped) track('pdp_v4_flip', { product: card.dataset.name || 'set' });
        return;
      }
      const dot = event.target.closest('[data-pdpv4-flip-go]');
      if (dot) {
        current = all[Number(dot.dataset.pdpv4FlipGo)] || current;
        layout();
      }
    });

    // Swipe: a horizontal drag of 40px moves one card (and is not also a tap).
    let startX = null;
    rail.addEventListener('pointerdown', (event) => {
      startX = event.clientX;
    });
    rail.addEventListener('pointerup', (event) => {
      if (startX === null) return;
      const delta = event.clientX - startX;
      startX = null;
      if (Math.abs(delta) < 40) return;
      swiped = true;
      step(delta < 0 ? 1 : -1);
    });

    window.matchMedia('(min-width: 990px)').addEventListener('change', layout);
    layout();
  }

  /* ---------- FAQ category chips (WONDR hair) ---------- */

  function initFaqFilter(root) {
    const chips = [...root.querySelectorAll('[data-pdpv4-faq-filter]')];
    if (!chips.length || root.dataset.pdpv4Ready) return;
    root.dataset.pdpv4Ready = '1';
    chips.forEach((chip) =>
      chip.addEventListener('click', () => {
        const category = chip.dataset.pdpv4FaqFilter;
        chips.forEach((other) => other.setAttribute('aria-pressed', String(other === chip)));
        root.querySelectorAll('[data-faq-category]').forEach((item) => {
          item.hidden = Boolean(category) && !item.dataset.faqCategory.split('|').includes(category);
        });
      }),
    );
  }

  /* ---------- Boot ---------- */

  function init(scope) {
    keepPreviewParam(scope);
    initCreator();
    scope.querySelectorAll('[data-pdpv4-gallery]').forEach(initGallery);
    scope.querySelectorAll('[data-pdpv4-buy]').forEach(initBuy);
    scope.querySelectorAll('.pdpv4-proof').forEach(initVideos);
    scope.querySelectorAll('[data-pdpv4-wall]').forEach(initWall);
    scope.querySelectorAll('[data-pdpv4-inline-video] video[data-poster]').forEach(initInlinePoster);
    scope.querySelectorAll('[data-pdpv4-pakket]').forEach(initPakket);
    scope.querySelectorAll('[data-pdpv4-reviews]').forEach(initReviews);
    scope.querySelectorAll('[data-pdpv4-flip]').forEach(initFlip);
    scope.querySelectorAll('.pdpv4-faq').forEach(initFaqFilter);
    initReviewSummary();
    initNav();
  }

  initAnchors();
  initAddButtons();
  initViewer();
  initInlineVideo();
  initClickTracking();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(document));
  } else {
    init(document);
  }
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
